import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Select,
  Snackbar,
  Typography,
} from '@mui/material';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import CodeEditor from './CodeEditor';
import EditorConsolePanel from './editor/EditorConsolePanel';
import WebPreviewPanel from './editor/WebPreviewPanel';
import ProgramizFileMenu from './editor/ProgramizFileMenu';
import { editorFileNameForLanguage } from './editor/EditorBreadcrumb';
import { PROGRAMIZ, programizRunButtonSx } from './editor/programizTheme';
import {
  executePracticeRun,
  resolveInteractivePrompts,
} from './editor/executionService';
import {
  createPracticeAutoSaveHandler,
  loadPracticeDraft,
} from './editor/draftManager';
import {
  downloadLocalSourceFile,
  formatFileStatusMessage,
  openLocalSourceFile,
  saveLocalSourceFile,
  saveLocalSourceFileAs,
  suggestedFileName,
  supportsNativeSavePicker,
} from './editor/localFileManager';
import SaveAsFallbackDialog from './editor/SaveAsFallbackDialog';
import {
  normalizeLanguage,
  resolveStarterCode,
  shouldApplyTemplate,
} from './editor/languageTemplates';
import { isWebPreviewLanguage } from './editor/htmlBoilerplate';
import {
  estimateInteractiveInputs,
  formatPromptForDisplay,
  formatResultSuffix,
  getRemainingStdout,
  getStudentPhone,
  LANGUAGE_LABELS,
  PRACTICE_LANGUAGES,
} from '../utils/practiceIdeUtils';

/**
 * Reusable Practice IDE — embed anywhere in the LMS (not tied to a chapter).
 *
 * ```jsx
 * <PracticeIDE persistenceKey="pgdca" fallbackRunContentId={42} />
 * ```
 */
export default function PracticeIDE({
  persistenceKey,
  fallbackRunContentId = null,
  languages = PRACTICE_LANGUAGES,
  defaultLanguage = 'c',
  showHint = true,
  showResetButton = true,
  hideRunButton = false,
  onRunControlsReady = null,
  embedded = false,
}) {
  const studentPhone = getStudentPhone();
  const autoSaveRef = useRef(null);
  const fileHandleRef = useRef(null);
  const [savedSnapshot, setSavedSnapshot] = useState(() =>
    resolveStarterCode(defaultLanguage, {}),
  );

  const [language, setLanguage] = useState(defaultLanguage);
  const [code, setCode] = useState(() => resolveStarterCode(defaultLanguage, {}));
  const [fileName, setFileName] = useState(() => editorFileNameForLanguage(defaultLanguage));
  const [transcript, setTranscript] = useState([]);
  const [currentInput, setCurrentInput] = useState('');
  const [waitingForInput, setWaitingForInput] = useState(false);
  const [interactiveSession, setInteractiveSession] = useState(null);
  const [runError, setRunError] = useState('');
  const [running, setRunning] = useState(false);
  const [fileBusy, setFileBusy] = useState(false);
  const [fileNotice, setFileNotice] = useState('');
  const [fileError, setFileError] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const canUseWindowsSaveAs = supportsNativeSavePicker();

  const isDirty = code !== savedSnapshot;
  const isPreviewLang = isWebPreviewLanguage(language);

  useEffect(() => {
    const draft = loadPracticeDraft(persistenceKey, studentPhone);
    if (draft?.code) {
      setCode(draft.code);
      setSavedSnapshot(draft.code);
      if (draft.language && languages.includes(draft.language)) {
        setLanguage(draft.language);
        setFileName(editorFileNameForLanguage(draft.language));
      }
    } else {
      setSavedSnapshot(resolveStarterCode(defaultLanguage, {}));
    }
  }, [persistenceKey, studentPhone, languages, defaultLanguage]);

  useEffect(() => {
    if (!isDirty) return undefined;
    const onBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  const confirmDiscardUnsaved = useCallback(() => {
    if (!isDirty) return true;
    return window.confirm(
      'You have unsaved changes. Discard them and continue?',
    );
  }, [isDirty]);

  const markSaved = useCallback((nextCode, nextFileName, nextHandle = fileHandleRef.current) => {
    setSavedSnapshot(nextCode);
    setFileName(nextFileName);
    fileHandleRef.current = nextHandle;
  }, []);

  const handleNewFile = useCallback(() => {
    if (!confirmDiscardUnsaved()) return;
    const starter = resolveStarterCode(language, {});
    setCode(starter);
    fileHandleRef.current = null;
    const nextName = editorFileNameForLanguage(language);
    markSaved(starter, nextName, null);
    setFileNotice('New file created.');
    setFileError('');
  }, [confirmDiscardUnsaved, language, markSaved]);

  const handleOpenFile = useCallback(async () => {
    if (!confirmDiscardUnsaved()) return;
    // Open picker on the click gesture before any busy-state setState.
    try {
      const result = await openLocalSourceFile();
      if (!result) return;
      setFileBusy(true);
      setFileError('');
      if (result.error) {
        setFileError(result.error);
        return;
      }
      setCode(result.code);
      if (result.language && languages.includes(result.language)) {
        setLanguage(result.language);
      }
      markSaved(result.code, result.fileName, result.fileHandle);
      setFileNotice(`Opened ${result.fileName} from your device.`);
    } catch (err) {
      setFileError(err?.message || 'Could not open file.');
    } finally {
      setFileBusy(false);
    }
  }, [confirmDiscardUnsaved, languages, markSaved]);

  const handleSaveFile = useCallback(async () => {
    // Open the native Save As dialog first (while user-gesture is still active).
    // Do not setState before the picker — Chromium requires a fresh gesture.
    try {
      if (!canUseWindowsSaveAs && !fileHandleRef.current) {
        setSaveDialogOpen(true);
        return;
      }
      const result = await saveLocalSourceFile({
        code,
        language,
        fileName,
        fileHandle: fileHandleRef.current,
      });
      if (!result) return;
      if (result.method === 'unsupported') {
        setSaveDialogOpen(true);
        return;
      }
      setFileBusy(true);
      setFileError('');
      markSaved(code, result.fileName, result.fileHandle);
      setFileNotice(formatFileStatusMessage(result));
    } catch (err) {
      setFileError(err?.message || 'Could not save file.');
    } finally {
      setFileBusy(false);
    }
  }, [canUseWindowsSaveAs, code, fileName, language, markSaved]);

  const handleSaveFileAs = useCallback(async () => {
    // Native Windows Save As must run on the click gesture before any setState.
    try {
      if (!canUseWindowsSaveAs) {
        setSaveDialogOpen(true);
        return;
      }
      const result = await saveLocalSourceFileAs({
        code,
        language,
        fileName: fileName || suggestedFileName(language),
      });
      if (!result) return;
      if (result.method === 'unsupported') {
        setSaveDialogOpen(true);
        return;
      }
      setFileBusy(true);
      setFileError('');
      markSaved(code, result.fileName, result.fileHandle);
      setFileNotice(formatFileStatusMessage(result));
    } catch (err) {
      setFileError(err?.message || 'Could not save file.');
    } finally {
      setFileBusy(false);
    }
  }, [canUseWindowsSaveAs, code, fileName, language, markSaved]);

  const handleFallbackSaveConfirm = useCallback(
    (finalName) => {
      setSaveDialogOpen(false);
      const result = downloadLocalSourceFile(code, finalName);
      markSaved(code, result.fileName, null);
      setFileNotice(formatFileStatusMessage(result));
      setFileError(
        'Firefox downloads to the Downloads folder. For Windows Save As (choose Desktop/Documents), open this page in Chrome or Edge.',
      );
    },
    [code, markSaved],
  );

  useEffect(() => {
    const onKeyDown = (event) => {
      const mod = event.ctrlKey || event.metaKey;
      if (!mod) return;
      const key = event.key.toLowerCase();
      // Block Chrome's default "Save page as" (shows page title + "Web Page, complete").
      if (key === 's' || key === 'o' || key === 'n') {
        event.preventDefault();
        event.stopPropagation();
        if (typeof event.stopImmediatePropagation === 'function') {
          event.stopImmediatePropagation();
        }
      }
      if (key === 's' && event.shiftKey) {
        handleSaveFileAs();
      } else if (key === 's') {
        handleSaveFile();
      } else if (key === 'o') {
        handleOpenFile();
      } else if (key === 'n') {
        handleNewFile();
      }
    };
    // Capture on window so we run before Monaco / Chrome page-save.
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [handleNewFile, handleOpenFile, handleSaveFile, handleSaveFileAs]);

  useEffect(() => {
    autoSaveRef.current = createPracticeAutoSaveHandler({
      courseCode: persistenceKey,
      studentPhone,
      getState: () => ({ language, code }),
    });
    autoSaveRef.current.start();
    return () => autoSaveRef.current?.stop();
  }, [persistenceKey, studentPhone, language, code]);

  const handleLanguageChange = (nextLanguage) => {
    const normalized = normalizeLanguage(nextLanguage);
    setLanguage(normalized);
    setPreviewHtml('');
    setTranscript([]);
    setRunError('');
    if (shouldApplyTemplate(code, {})) {
      const starter = resolveStarterCode(normalized, {});
      setCode(starter);
      if (!fileHandleRef.current) {
        setFileName(editorFileNameForLanguage(normalized));
      }
    }
  };

  const handleReset = () => {
    if (
      !window.confirm(
        'Reset code to the default starter template for this language?',
      )
    ) {
      return;
    }
    setCode(resolveStarterCode(language, {}));
  };

  const finishInteractiveRun = useCallback(async (session) => {
    const formatted = await executePracticeRun({
      language,
      code,
      stdin: session.collectedInputs.join('\n'),
      studentPhone,
      fallbackContentId: fallbackRunContentId,
      skipStdinCheck: true,
    });

    const suffix = formatResultSuffix(
      getRemainingStdout(formatted.output, session.prompts),
    );

    if (suffix) {
      setTranscript((prev) => [...prev, { type: 'out', text: suffix }]);
    }
    setRunError(formatted.error);
    setWaitingForInput(false);
    setInteractiveSession(null);
    setCurrentInput('');
  }, [language, code, studentPhone, fallbackRunContentId]);

  const handleRun = useCallback(async () => {
    setRunning(true);
    setRunError('');
    setTranscript([]);
    setCurrentInput('');
    setWaitingForInput(false);
    setInteractiveSession(null);

    try {
      if (isWebPreviewLanguage(language)) {
        setPreviewHtml(code);
        return;
      }

      if (!studentPhone) {
        throw new Error('Please log in again to run code.');
      }

      const inputCount = estimateInteractiveInputs(code, language);

      if (inputCount <= 0) {
        const formatted = await executePracticeRun({
          language,
          code,
          stdin: '',
          studentPhone,
          fallbackContentId: fallbackRunContentId,
          skipStdinCheck: true,
        });
        if (formatted.output) {
          setTranscript([{ type: 'out', text: formatted.output }]);
        }
        setRunError(formatted.error);
        return;
      }

      const prompts = await resolveInteractivePrompts({
        language,
        code,
        studentPhone,
        fallbackContentId: fallbackRunContentId,
      });

      const session = {
        prompts,
        collectedInputs: [],
        step: 0,
      };
      setInteractiveSession(session);

      const firstPrompt = formatPromptForDisplay(prompts[0] || '');
      if (firstPrompt) {
        setTranscript([{ type: 'out', text: firstPrompt }]);
      }
      setWaitingForInput(true);
    } catch (err) {
      setRunError(err?.message || 'Run failed.');
    } finally {
      setRunning(false);
    }
  }, [language, code, studentPhone, fallbackRunContentId]);

  const handleClearOutput = useCallback(() => {
    setTranscript([]);
    setRunError('');
    setPreviewHtml('');
    setCurrentInput('');
    setWaitingForInput(false);
    setInteractiveSession(null);
  }, []);

  const handleSubmitInput = useCallback(async (rawValue) => {
    if (!interactiveSession || !waitingForInput) return;

    const value = String(rawValue ?? '').trim();
    if (!value) return;

    const nextInputs = [...interactiveSession.collectedInputs, value];
    const nextStep = interactiveSession.step + 1;
    const inputCount = estimateInteractiveInputs(code, language);

    setTranscript((prev) => [...prev, { type: 'in', text: `${value}\n` }]);
    setCurrentInput('');

    if (nextStep < inputCount) {
      const nextPrompt = formatPromptForDisplay(interactiveSession.prompts[nextStep] || '');
      if (nextPrompt) {
        setTranscript((prev) => [...prev, { type: 'out', text: nextPrompt }]);
      }
      setInteractiveSession({
        ...interactiveSession,
        collectedInputs: nextInputs,
        step: nextStep,
      });
      return;
    }

    setWaitingForInput(false);
    setRunning(true);
    setRunError('');

    try {
      await finishInteractiveRun({
        ...interactiveSession,
        collectedInputs: nextInputs,
      });
    } catch (err) {
      setRunError(err?.message || 'Run failed.');
      setWaitingForInput(true);
      setInteractiveSession({
        ...interactiveSession,
        collectedInputs: nextInputs,
        step: nextStep,
      });
    } finally {
      setRunning(false);
    }
  }, [
    interactiveSession,
    waitingForInput,
    code,
    language,
    finishInteractiveRun,
  ]);

  useEffect(() => {
    if (!onRunControlsReady) return undefined;
    onRunControlsReady({
      handleRun,
      running,
      disabled: isPreviewLang ? running : running || !studentPhone,
      label: isPreviewLang
        ? running
          ? 'Previewing...'
          : 'Preview'
        : running
          ? 'Running...'
          : 'Run',
    });
    return () => onRunControlsReady(null);
  }, [onRunControlsReady, handleRun, running, studentPhone, isPreviewLang]);

  const displayFileName = isDirty ? `${fileName} *` : fileName;

  const content = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: embedded ? '100%' : 'auto',
        minHeight: embedded ? 0 : 520,
        bgcolor: PROGRAMIZ.bodyBg,
      }}
    >
      {fileError ? (
        <Alert severity="error" onClose={() => setFileError('')} sx={{ flexShrink: 0, mx: embedded ? 0 : 0, borderRadius: 0 }}>
          {fileError}
        </Alert>
      ) : null}

      {!canUseWindowsSaveAs ? (
        <Alert severity="warning" sx={{ flexShrink: 0, borderRadius: 0 }}>
          Windows Save As (folder choose) needs{' '}
          <strong>Google Chrome</strong> or <strong>Microsoft Edge</strong>.
          Firefox lo Save press cheste Downloads folder ki download avutundi.
          Open this same URL in Chrome/Edge for Desktop / Documents picker.
        </Alert>
      ) : null}

      {!studentPhone ? (
        <Alert severity="warning" sx={{ flexShrink: 0, borderRadius: 0 }}>
          Student session is missing. Please login again.
        </Alert>
      ) : null}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          border: `1px solid ${PROGRAMIZ.border}`,
          borderRadius: embedded ? 0 : 1,
          overflow: 'hidden',
          bgcolor: PROGRAMIZ.bodyBg,
        }}
      >
        {/* Editor panel — left */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: { xs: 280, md: 0 },
            display: 'flex',
            flexDirection: 'column',
            borderRight: { md: `1px solid ${PROGRAMIZ.border}` },
            borderBottom: { xs: `1px solid ${PROGRAMIZ.border}`, md: 'none' },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              px: { xs: 1, sm: 1.5 },
              minHeight: 44,
              bgcolor: PROGRAMIZ.headerBg,
              borderBottom: `1px solid ${PROGRAMIZ.border}`,
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  px: 1.5,
                  py: 0.75,
                  borderBottom: `2px solid ${PROGRAMIZ.tabActive}`,
                  bgcolor: PROGRAMIZ.bodyBg,
                  flexShrink: 0,
                }}
              >
                <Typography
                  variant="body2"
                  noWrap
                  sx={{ color: PROGRAMIZ.text, fontWeight: 500, fontSize: 14, maxWidth: 160 }}
                >
                  {displayFileName}
                </Typography>
              </Box>

              <ProgramizFileMenu
                busy={fileBusy}
                onNew={handleNewFile}
                onOpen={handleOpenFile}
                onSave={handleSaveFile}
                onSaveAs={handleSaveFileAs}
              />

              {languages.length > 1 ? (
                <Select
                  size="small"
                  value={language}
                  onChange={(e) => handleLanguageChange(e.target.value)}
                  variant="standard"
                  disableUnderline
                  sx={{
                    color: PROGRAMIZ.muted,
                    fontSize: 13,
                    minWidth: 72,
                    '& .MuiSelect-select': { py: 0.25, pr: 2.5 },
                    '& .MuiSvgIcon-root': { color: PROGRAMIZ.muted, fontSize: 18 },
                  }}
                >
                  {languages.map((lang) => (
                    <MenuItem key={lang} value={lang} sx={{ fontSize: 13 }}>
                      {LANGUAGE_LABELS[lang] || lang.toUpperCase()}
                    </MenuItem>
                  ))}
                </Select>
              ) : null}
            </Box>

            {!hideRunButton ? (
              <Button
                variant="contained"
                size="small"
                startIcon={
                  isPreviewLang ? (
                    <VisibilityRoundedIcon sx={{ fontSize: '18px !important' }} />
                  ) : (
                    <PlayArrowRoundedIcon sx={{ fontSize: '18px !important' }} />
                  )
                }
                disabled={isPreviewLang ? running : running || !studentPhone}
                onClick={handleRun}
                sx={programizRunButtonSx}
              >
                {isPreviewLang
                  ? running
                    ? 'Previewing...'
                    : 'Preview'
                  : running
                    ? 'Running...'
                    : 'Run'}
              </Button>
            ) : null}
          </Box>

          <Box sx={{ flex: 1, minHeight: 0 }}>
            <CodeEditor
              language={language}
              value={code}
              onChange={setCode}
              contentId={`practice-${persistenceKey}`}
              fileName={fileName}
              showToolbar={false}
              showStatusBar={false}
              showBreadcrumb={false}
              themeId="vs-light"
              fillHeight
              onSave={handleSaveFile}
              onSaveAs={handleSaveFileAs}
            />
          </Box>
        </Box>

        {/* Output / Preview panel — right */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: { xs: 220, md: 0 },
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {isPreviewLang ? (
            <WebPreviewPanel
              language={language}
              code={code}
              fillHeight
              variant="programiz"
            />
          ) : (
            <EditorConsolePanel
              variant="programiz"
              fillHeight
              interactive
              transcript={transcript}
              error={runError}
              running={running}
              waitingForInput={waitingForInput}
              currentInput={currentInput}
              onCurrentInputChange={setCurrentInput}
              onSubmitInput={handleSubmitInput}
              onClear={handleClearOutput}
            />
          )}
        </Box>
      </Box>

      <Snackbar
        open={Boolean(fileNotice)}
        autoHideDuration={4000}
        onClose={() => setFileNotice('')}
        message={fileNotice}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      <SaveAsFallbackDialog
        open={saveDialogOpen}
        language={language}
        fileName={fileName}
        onCancel={() => setSaveDialogOpen(false)}
        onConfirm={handleFallbackSaveConfirm}
      />
    </Box>
  );

  if (embedded) {
    return content;
  }

  return (
    <Box sx={{ py: 3, px: 2 }}>
      {content}
    </Box>
  );
}
